import { derived, readable } from "svelte/store";
import { formatTime } from ".";
import { CheckpointState } from "../Model/CheckpointState";
import { State } from "../Model/State";
import { gameFactory } from "./gameFactory";
import { calcDistance, playerPosition } from "./geolocation";
const hasSavedGame = new State(!!localStorage.getItem('has_saved_game'));
const hasSavedGameReadable = readable(false, set => hasSavedGame.subscribe(set));
const currentTarget = new State(loadTarget());
const gamePoints = new State(getPoints());
const gamePointsReadable = readable(0, set => gamePoints.subscribe(set));
const gameClock = readable('', set => {
    const intervalId = window.setInterval(() => {
        const gameStart = localStorage.getItem('game_start_date');
        if (!gameStart) {
            set('');
            return;
        }
        const endDate = localStorage.getItem('game_end_date');
        const gameStartDate = new Date(gameStart);
        const refDate = endDate ? new Date(endDate) : new Date();
        set(formatTime(Math.floor((refDate.getTime() - gameStartDate.getTime()) / 1000)));
    }, 1000);
    return () => window.clearInterval(intervalId);
});
gamePoints.subscribe(savePoints);
export { hasSavedGameReadable as hasSavedGame, gamePointsReadable as gamePoints, currentTarget, gameClock, };
export function saveGame(game) {
    localStorage.setItem('saved_game', JSON.stringify(game));
    localStorage.setItem('has_saved_game', '1');
    hasSavedGame.set(true);
}
export function loadGame() {
    if (!hasSavedGame.value) {
        return null;
    }
    const gameDto = JSON.parse(localStorage.getItem('saved_game'));
    return gameFactory(gameDto);
}
export function setTarget(target) {
    var _a;
    if (((_a = currentTarget.value) === null || _a === void 0 ? void 0 : _a.checkpointId) === target.checkpointId) {
        return;
    }
    localStorage.setItem('current_target', JSON.stringify(target));
    currentTarget.set(target);
}
export function setCheckpointAsTarget({ id, position }) {
    setTarget({
        checkpointId: id,
        position
    });
}
export function clearTarget() {
    if (currentTarget.value === null) {
        return;
    }
    localStorage.removeItem('current_target');
    currentTarget.set(null);
}
function loadTarget() {
    const json = localStorage.getItem('current_target');
    if (!json) {
        return null;
    }
    return JSON.parse(json);
}
export function setStartGameDate() {
    localStorage.setItem('game_start_date', (new Date).toISOString());
}
export function setEndGameDate() {
    localStorage.setItem('game_end_date', (new Date).toISOString());
}
export function clearGameDates() {
    localStorage.removeItem('game_start_date');
    localStorage.removeItem('game_end_date');
}
export function clearPoints() {
    gamePoints.set(0);
}
export function addPoints(points) {
    gamePoints.set(gamePoints.value + points);
}
export function quitPoints(points) {
    gamePoints.set(Math.max(0, gamePoints.value - points));
}
function savePoints(points) {
    localStorage.setItem('game_points', `${points}`);
}
function getPoints() {
    const points = localStorage.getItem('game_points');
    return parseInt(points || '0');
}
export function createGameState(game) {
    const checkpointsMap = new Map();
    const checkpointStates = game.checkpoints.map(checkpoint => {
        const checkpointState = new CheckpointState(checkpoint.id, checkpoint.position, new State(checkpoint.isCompleted.value), new State(checkpoint.isVisible.value), new State(checkpoint.isAccesible.value));
        checkpointsMap.set(checkpoint.id, checkpoint);
        return checkpointState;
    });
    for (const checkpointState of checkpointStates) {
        const checkpoint = checkpointsMap.get(checkpointState.id);
        const mustVisible = createAccessibilityStatementReadable(checkpointStates, checkpoint.accesibility.visibleOn);
        const mustAccesible = createAccessibilityStatementReadable(checkpointStates, checkpoint.accesibility.accesibleOn);
        mustVisible.subscribe(value => checkpointState.setVisible(value));
        mustAccesible.subscribe(value => checkpointState.setAccesible(value));
    }
    return checkpointStates;
}
export function createAccessibilityStatementReadable(states, accessibilityStatements) {
    const accesibilityReadables = accessibilityStatements.map(accessibilityStatement => {
        const targets = states.filter(({ id }) => accessibilityStatement.targets.includes(id));
        const watch = [];
        targets.forEach(target => {
            if ('isVisible' in accessibilityStatement) {
                watch.push(derived(target.isVisible, isVisible => isVisible === accessibilityStatement.isVisible));
            }
            if ('isAccesible' in accessibilityStatement) {
                watch.push(derived(target.isAccesible, isAccesible => isAccesible === accessibilityStatement.isAccesible));
            }
            if ('isCompleted' in accessibilityStatement) {
                watch.push(derived(target.isCompleted, isCompleted => isCompleted === accessibilityStatement.isCompleted));
            }
            if ('distance' in accessibilityStatement) {
                watch.push(derived(playerPosition, createDistanceChecker(accessibilityStatement.distance, target.position)));
            }
        });
        return derived(watch, values => values.every(value => value));
    });
    return derived(accesibilityReadables, values => values.every(value => value));
}
function createDistanceChecker(maxDistance, from) {
    return (position) => {
        if (!position) {
            return false;
        }
        const distance = calcDistance(position, from);
        return distance <= maxDistance;
    };
}
